/**
 * NOS-51, NOS-52: Hooks for blog categories.
 * useBlogCategories fetches list with post counts; NOS-52 adds create/update/delete.
 */

import { useCallback, useEffect, useState } from "react"
import { supabase } from "@/lib/supabase"
import type { Database } from "@/types/database"
import { generateSlug } from "@/lib/slugUtils"

type BlogCategory = Database["public"]["Tables"]["blog_categories"]["Row"]
type BlogCategoryInsert = Database["public"]["Tables"]["blog_categories"]["Insert"]
type BlogCategoryUpdate = Database["public"]["Tables"]["blog_categories"]["Update"]

export interface BlogCategoryWithCount extends BlogCategory {
  post_count: number
}

const DEFAULT_COLORS = [
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
]

export function useBlogCategories(orgId: string | null) {
  const [categories, setCategories] = useState<BlogCategoryWithCount[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!orgId) {
      setCategories([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    const { data: catData, error: fetchError } = await supabase
      .from("blog_categories")
      .select("*")
      .eq("org_id", orgId)
      .order("name")
    if (fetchError) {
      setError(fetchError.message)
      setCategories([])
      setLoading(false)
      return
    }

    const cats = (catData ?? []) as BlogCategory[]
    const ids = cats.map((c) => c.id)
    const countMap: Record<string, number> = {}
    ids.forEach((id) => (countMap[id] = 0))

    if (ids.length > 0) {
      const { data: postData } = await supabase
        .from("blog_posts")
        .select("category_id")
        .eq("org_id", orgId)
        .not("category_id", "is", null)
      const posts = (postData ?? []) as { category_id: string }[]
      posts.forEach((p) => {
        if (p.category_id && countMap[p.category_id] !== undefined) {
          countMap[p.category_id]++
        }
      })
    }

    setCategories(
      cats.map((c) => ({ ...c, post_count: countMap[c.id] ?? 0 }))
    )
    setLoading(false)
  }, [orgId])

  useEffect(() => {
    refresh()
  }, [refresh])

  return { categories, loading, error, refresh }
}

export function useCreateBlogCategory(orgId: string | null) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const createBlogCategory = useCallback(
    async (input: { name: string; slug?: string; description?: string | null; color?: string | null }): Promise<BlogCategory | null> => {
      if (!orgId) return null
      setLoading(true)
      setError(null)
      const slug = input.slug?.trim() || generateSlug(input.name) || "category"
      const insert: BlogCategoryInsert = {
        org_id: orgId,
        name: input.name.trim(),
        slug,
        description: input.description?.trim() || null,
        color: input.color || null,
      }
      const { data, error: insertError } = await supabase
        .from("blog_categories")
        .insert(insert)
        .select()
        .single()
      setLoading(false)
      if (insertError) {
        setError(insertError.code === "23505" ? "A category with this URL already exists." : insertError.message)
        return null
      }
      return data as BlogCategory
    },
    [orgId]
  )

  return { createBlogCategory, loading, error }
}

export function useUpdateBlogCategory(orgId: string | null) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const updateBlogCategory = useCallback(
    async (id: string, updates: BlogCategoryUpdate): Promise<boolean> => {
      if (!orgId) return false
      setLoading(true)
      setError(null)
      const { error: updateError } = await supabase
        .from("blog_categories")
        .update(updates)
        .eq("id", id)
        .eq("org_id", orgId)
      setLoading(false)
      if (updateError) {
        setError(updateError.code === "23505" ? "A category with this URL already exists." : updateError.message)
        return false
      }
      return true
    },
    [orgId]
  )

  return { updateBlogCategory, loading, error }
}

export function useDeleteBlogCategory(orgId: string | null) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const deleteBlogCategory = useCallback(
    async (categoryId: string, reassignToCategoryId?: string | null): Promise<boolean> => {
      if (!orgId) return false
      setLoading(true)
      setError(null)

      const { data: posts } = await supabase
        .from("blog_posts")
        .select("id")
        .eq("org_id", orgId)
        .eq("category_id", categoryId)

      const postIds = (posts ?? []).map((p: { id: string }) => p.id)
      if (postIds.length > 0) {
        const updates = reassignToCategoryId
          ? { category_id: reassignToCategoryId }
          : { category_id: null }
        const { error: updateErr } = await supabase
          .from("blog_posts")
          .update(updates)
          .in("id", postIds)
        if (updateErr) {
          setError(updateErr.message)
          setLoading(false)
          return false
        }
      }

      const { error: deleteError } = await supabase
        .from("blog_categories")
        .delete()
        .eq("id", categoryId)
        .eq("org_id", orgId)
      setLoading(false)
      if (deleteError) {
        setError(deleteError.message)
        return false
      }
      return true
    },
    [orgId]
  )

  return { deleteBlogCategory, loading, error }
}

export { DEFAULT_COLORS }
